import { DropdownMenu, DropdownMenuSize } from "react-migration/components/DropdownMenu";
import { SiteFiltersStore, buildSiteFiltersStoreMethods } from "./SiteFiltersStore/store";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import logSiteCardEvent, { SiteFiltersView } from "react-migration/lib/util/logSiteCardEvent";

import { AssigneesFilter } from "./components/AssigneesFilter";
import { Button } from "react-migration/components/Button";
import { ClearFiltersButton } from "./components/ClearFiltersButton";
import { SelectFilterStatus } from "react-migration/components/SelectFilter";
import { SiteLabelsFilter } from "./components/SiteLabelsFilter";
import { SiteLocalAuthorityFilter } from "./components/SiteLocalAuthorityFilter";
import { SiteSearch } from "./components/SiteSearch";
import { StagesFilter } from "./components/StagesFilter";
import { SiteFilters as TSiteFilters } from "../../typings/SiteFilters";
import { noop } from "lodash";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import clsx from "clsx";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";

export type OnFilterUpdateCallback = (filter: keyof TSiteFilters) => void;

export type OnFiltersClearedCallback = () => void;

export type SiteFiltersEventMetadata = {
  view: SiteFiltersView;
};

export type VisibilityType = {
  siteCount: number;
  totalSiteCount: number;
  showingStageCount: number;
  totalStageCount: number;
};

export type SiteFiltersProps = {
  /** Essential metadata for Site Filters mixpanel event logging */
  eventMetadata: SiteFiltersEventMetadata;
  /** Which store to use */
  store: SiteFiltersStore;
  visibility?: VisibilityType;
  /**
   * Callback to be called when the filters are updated
   */
  onFiltersUpdated?: () => void;
  /**
   * The layout of the filters
   */
  filterLayout?: "row" | "column";
};

/** Context for all *INTERNAL* rendering concerns  to the Site Filters
 * For example, setting up common functions that contain context from consumers for event logging
 *
 * If state must be shared with consumers of SiteFilters then please use the Valtio SiteFiltersStore
 * For example, the values of the filters
 */
export const SiteFiltersContext = createContext<
  {
    onFilterUpdate: OnFilterUpdateCallback;
    onFiltersCleared: OnFiltersClearedCallback;
  } & ReturnType<typeof buildSiteFiltersStoreMethods>
>({
  onFilterUpdate: noop,
  onFiltersCleared: noop,
  useAssignees: () => [],
  updateAssignees: noop,
  useLocalAuthorities: () => [],
  updateLocalAuthorities: noop,
  useSearchString: () => "",
  updateSearchString: noop,
  useSiteLabels: () => [],
  updateSiteLabels: noop,
  useStageIds: () => [],
  updateStageIds: noop,
  resetSiteFiltersStore: noop,
});

export const SiteFilters = ({
  eventMetadata: { view },
  visibility,
  store,
  onFiltersUpdated,
  filterLayout = "row",
}: SiteFiltersProps) => {
  const [showLocalAuthorityModal, setShowLocalAuthorityModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [showAssigneesModal, setShowAssigneesModal] = useState(false);
  const [showStagesModal, setShowStagesModal] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);

  const onFilterUpdate: OnFilterUpdateCallback = useCallback(
    (filter) => {
      logSiteCardEvent({
        name: "Site Filters - Filter Updated",
        context: {
          view,
          filter,
        },
      });
      onFiltersUpdated?.();
    },
    [view, onFiltersUpdated]
  );

  const onFiltersCleared: OnFiltersClearedCallback = useCallback(() => {
    logSiteCardEvent({
      name: "Site Filters - Filters Cleared",
      context: {
        view,
      },
    });
    onFiltersUpdated?.();
  }, [view, onFiltersUpdated]);

  const contextValues = useMemo(() => {
    const storeMethods = buildSiteFiltersStoreMethods(store);
    return { onFilterUpdate, onFiltersCleared, ...storeMethods };
  }, [onFilterUpdate, onFiltersCleared, store]);

  return (
    <SiteFiltersContext.Provider value={contextValues}>
      <div
        data-testid="site-filters"
        className={clsx("flex items-center shrink-0 gap-2", {
          "flex-row": filterLayout === "row",
          "flex-row lg:flex-col lg:items-stretch": filterLayout === "column",
        })}
      >
        <SiteSearch
          className={clsx({
            "max-w-36 md:max-w-fit": filterLayout === "row",
            "w-full": filterLayout === "column",
          })}
          isOpen={openSearch}
          setIsOpen={setOpenSearch}
        />

        <div
          className={clsx("hidden lg:flex gap-2", {
            "lg:flex-row": filterLayout === "row",
            "lg:flex-col lg:justify-stretch": filterLayout === "column",
          })}
        >
          <SiteLocalAuthorityFilter type="desktop" className="justify-between" />
          <SiteLabelsFilter type="desktop" className="justify-between" />
          <AssigneesFilter type="desktop" className="justify-between" />
          <StagesFilter type="desktop" className="justify-between" />
          <ClearFiltersButton />
          <div className="hidden 2xl:flex pl-2 border-l border-border-base-divider">
            {visibility && <VisibilityDetails visibility={visibility} />}
          </div>
        </div>

        <div
          className={clsx("flex lg:hidden gap-2", {
            "flex-col": filterLayout === "column",
            "flex-row": filterLayout === "row",
          })}
        >
          <SiteLocalAuthorityFilter
            type="mobile"
            openModal={showLocalAuthorityModal}
            setOpenModal={setShowLocalAuthorityModal}
          />
          <SiteLabelsFilter type="mobile" openModal={showLabelsModal} setOpenModal={setShowLabelsModal} />
          <AssigneesFilter type="mobile" openModal={showAssigneesModal} setOpenModal={setShowAssigneesModal} />
          <StagesFilter type="mobile" openModal={showStagesModal} setOpenModal={setShowStagesModal} />
          <MobileFilterDropdown
            setShowLocalAuthorityModal={setShowLocalAuthorityModal}
            setShowLabelsModal={setShowLabelsModal}
            setShowAssigneesModal={setShowAssigneesModal}
            setShowStagesModal={setShowStagesModal}
            visibility={visibility}
          />
          <ClearFiltersButton />
        </div>
      </div>
    </SiteFiltersContext.Provider>
  );
};

type MobileFilterDropdownType = {
  setShowLocalAuthorityModal: (v: boolean) => void;
  setShowLabelsModal: (v: boolean) => void;
  setShowAssigneesModal: (v: boolean) => void;
  setShowStagesModal: (v: boolean) => void;
  visibility?: VisibilityType;
};

const MobileFilterDropdown = ({
  setShowLocalAuthorityModal,
  setShowLabelsModal,
  setShowAssigneesModal,
  setShowStagesModal,
  visibility,
}: MobileFilterDropdownType) => {
  const { t } = useTranslation();
  const { useAssignees, useLocalAuthorities, useStageIds, useSiteLabels } = useContext(SiteFiltersContext);
  const isMobile = useIsMobile();

  return (
    <div>
      <DropdownMenu.Provider size={DropdownMenuSize.AUTO}>
        <DropdownMenu.Trigger data-testid="sites-filters-trigger" align={DropdownMenu.TriggerAlignment["BOTTOM_RIGHT"]}>
          <Button leadIcon="lt-filter-line" variant="ghost" iconSize={isMobile ? "2xl" : undefined} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="text-sm">
          {visibility && (
            <div className="pt-2 px-2">
              <VisibilityDetails visibility={visibility} />
            </div>
          )}
          <DropdownMenu.Item
            onClick={() => setShowLocalAuthorityModal(true)}
            className="flex justify-between items-center"
          >
            <span>{t("sites.pipeline.filters.local_authority")}</span>
            <SelectFilterStatus selectedOptionIds={useLocalAuthorities()} />
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => setShowLabelsModal(true)} className="flex justify-between items-center">
            <span>{t("sites.pipeline.filters.select_labels")}</span>
            <SelectFilterStatus selectedOptionIds={useSiteLabels()} />
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => setShowAssigneesModal(true)} className="flex justify-between items-center">
            <span>{t("sites.pipeline.filters.select_assignees")}</span>
            <SelectFilterStatus selectedOptionIds={useAssignees()} />
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={() => setShowStagesModal(true)} className="flex justify-between items-center">
            <span>{t("sites.pipeline.filters.select_stages")}</span>
            <SelectFilterStatus selectedOptionIds={useStageIds()} />
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Provider>
    </div>
  );
};

const VisibilityDetails = ({ visibility }: { visibility: VisibilityType }) => {
  const { t } = useTranslation();
  return (
    <div className="self-center flex-initial text-xs lg:text-sm p-2 text-text-base-secondary-a">
      {t("sites.pipeline.stages.filters.stages_filters.visibility_indicator", {
        siteCountDisplay: (
          <strong className="px-0.5">{`${visibility.siteCount || 0}/${visibility.totalSiteCount || 0}`}</strong>
        ),
        stageCountDisplay: (
          <strong className="px-0.5">{`${visibility.showingStageCount || 0}/${
            visibility.totalStageCount || 0
          }`}</strong>
        ),
      })}
    </div>
  );
};
